import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import DS from 'ember-data';
import CampaignNavStepController from 'cio/controllers/campaign-nav-step';
import { dropTask, task } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'cio/models/campaign';

export default class IndexController extends CampaignNavStepController {
  @service auth!: AuthService;
  @service store!: DS.Store;

  isCampaignOverview = false;

  originalSendToUnsubscribed = false;
  originalSubscriptionTopicId: string | null = null;

  @tracked messageLimitsRetryWindowSecsIsValid = true;

  declare model: CampaignModel;

  @computed('isSendToUnsubscribedDirty', 'isSubscriptionTopicDirty', 'model.hasDirtyAttributes')
  get isDirty() {
    return this.isSendToUnsubscribedDirty || this.isSubscriptionTopicDirty || this.model.hasDirtyAttributes;
  }

  @computed('messageLimitsRetryWindowSecsIsValid')
  get isInvalid() {
    return !this.messageLimitsRetryWindowSecsIsValid;
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  @computed('originalSendToUnsubscribed', 'model.sendToUnsubscribed')
  private get isSendToUnsubscribedDirty() {
    return this.originalSendToUnsubscribed !== this.model.sendToUnsubscribed;
  }

  @computed('originalSubscriptionTopicId', 'model.subscriptionTopic.id')
  private get isSubscriptionTopicDirty() {
    return this.originalSubscriptionTopicId !== this.model.subscriptionTopicId;
  }

  @action
  onAudienceChange(subscriptionTopicId: string | null, sendToUnsubscribed: boolean) {
    this.model.setProperties({
      subscriptionTopicId,
      sendToUnsubscribed,
    });
  }

  @action
  onUseMessageLimitsChange(enabled: boolean) {
    this.model.set('useMessageLimits', enabled);
  }

  @action
  onUseWorkspaceMessageLimitsRetryChange(enabled: boolean) {
    this.model.set('useWorkspaceMessageLimitsRetry', enabled);
  }

  @action
  onMessageLimitsRetryWindowSecsChange(seconds: number) {
    this.model.set('messageLimitsRetryWindowSecs', seconds);
  }

  @action
  onMessageLimitsRetryWindowSecsValidate(valid: boolean) {
    this.messageLimitsRetryWindowSecsIsValid = valid;
  }

  saveCampaignTask = dropTask(async () => {
    let { model } = this;

    this.setProperties({
      originalSubscriptionTopicId: model.subscriptionTopicId,
      originalSendToUnsubscribed: model.sendToUnsubscribed,
    });

    this.model.setProperties({ updateType: 'global_settings' });
    await model.save();
  });

  resetTask = task(async () => {
    await this.model.rollbackAttributes();
  });
}
